import { Box, Text, Badge, Loader } from '@mantine/core';
import { IconDiscount2 } from '@tabler/icons-react';
import { formatPrice } from '../../utils/formatPrice';
import { effectiveCustomerPrice } from '../../utils/adminCustomerPrice';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Client-side summary of which lines get a customer-specific price (customer_prices).
// Mirrors applyCustomerPrices / getDiscountedPrice so what the admin sees here
// matches what checkout would charge. Purely informational — never blocks.
export default function CustomerPricingPanel({ loading, items, variantLookup, customerPricesByVariant, t }) {
  const entries = [];
  for (const it of items) {
    if (it.type === 'adhoc' || !it.variant_id) continue;
    const v = variantLookup.get(it.variant_id);
    const cp = customerPricesByVariant.get(it.variant_id);
    if (!v || !cp) continue;
    const storePrice = v.price != null ? Number(v.price) : null;
    if (storePrice == null) continue;
    const effective = effectiveCustomerPrice(v, cp, Math.max(0, Number(it.quantity) || 0));
    if (effective == null) continue;
    if (round2(effective) === round2(storePrice)) continue;
    entries.push({ v, cp, it, storePrice, effective });
  }

  if (loading) {
    return (
      <Box className="take-order-offers" aria-live="polite" role="status">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">{t('takeOrderCustomerPricingLoading', 'Checking customer pricing…')}</Text>
      </Box>
    );
  }

  if (entries.length === 0) {
    return (
      <Box className="take-order-offers take-order-offers--empty">
        <Text size="sm" c="dimmed">
          {t('takeOrderCustomerPricingNone', 'No customer-specific pricing applies to these items.')}
        </Text>
      </Box>
    );
  }

  return (
    <Box className="take-order-offers" aria-live="polite">
      <Box className="take-order-offers__head">
        <Text size="sm" fw={600}>
          <IconDiscount2 size={16} style={{ verticalAlign: '-3px', marginRight: 6 }} aria-hidden="true" />
          {t('takeOrderCustomerPricing', 'Customer pricing')}
        </Text>
      </Box>

      {entries.map(({ v, cp, it, storePrice, effective }) => {
        const label = it.name || v.productName || v.sku || '';
        const isVolume = cp.pricing_model === 'volume';
        const diff = round2(storePrice - effective);
        const pct = storePrice > 0 ? Math.round((diff / storePrice) * 100) : 0;
        const saving = diff >= 0;

        if (isVolume) {
          const base = cp.price != null ? Number(cp.price) : null;
          const post = cp.post_threshold_price != null ? Number(cp.post_threshold_price) : null;
          let aboveThreshold = true;
          if (base != null && post != null && base !== post && effective === base) aboveThreshold = false;
          const threshold = cp.volume_threshold != null ? Number(cp.volume_threshold) : 0;
          const unit = cp.volume_type === 'weight'
            ? t('takeOrderThresholdUnitKg', 'kg')
            : t('takeOrderThresholdUnitUnits', 'units');
          const position = aboveThreshold
            ? t('takeOrderAbove', 'above')
            : t('takeOrderBelow', 'below');

          return (
            <div className="take-order-price-row" key={v.id || v.sku}>
              <Box className="take-order-price-row__head">
                <Text size="sm" fw={500}>{label}</Text>
                <Badge size="xs" color="teal" variant="light">
                  {t('takeOrderCustomerPricingVolumeApplied', 'volume pricing applied')}
                </Badge>
              </Box>
              <Text size="xs" className="take-order-price-row__detail">
                <span className="discount-value">{formatPrice(effective)}</span>
                {' '}{position} {threshold} {unit} ·{' '}
                {saving ? (
                  <>
                    {t('takeOrderSaving', 'saving')} <span className="discount-value">{pct}%</span>
                  </>
                ) : (
                  <span className="take-order-price-row__over">{pct}% {t('takeOrderAboveStore', 'above store price')}</span>
                )}
              </Text>
            </div>
          );
        }

        return (
          <div className="take-order-price-row" key={v.id || v.sku}>
            <Box className="take-order-price-row__head">
              <Text size="sm" fw={500}>{label}</Text>
              <Badge size="xs" color="teal" variant="light">
                {t('takeOrderCustomerPricingApplied', 'special pricing applied')}
              </Badge>
            </Box>
            <Text size="xs" className="take-order-price-row__detail">
              <span className="discount-value">{formatPrice(effective)}</span>
              {' '}{t('takeOrderInsteadOf', 'instead of')} {formatPrice(storePrice)} ·{' '}
              {saving ? (
                <>
                  {t('takeOrderSaving', 'saving')} <span className="discount-value">{pct}%</span>
                </>
              ) : (
                <span className="take-order-price-row__over">{pct}% {t('takeOrderAboveStore', 'above store price')}</span>
              )}
            </Text>
          </div>
        );
      })}
    </Box>
  );
}
